<template>
  <div>
    <app-title-bar>
      <app-material-icon name="home_storage" />
      <h2 class="text-text-primary">{{ $t('subscriptions.views.SubscriptionSignaturesConsumption.title') }}</h2>
    </app-title-bar>
    <div class="d-flex flex-column row-gap-8 layout-spacing">
      <AppCard class="pa-6 rounded-lg d-flex flex-column row-gap-6" elevation="0">
        <div class="d-flex flex-column row-gap-1">
          <h3 class="text--medium text--bold text-text-primary">{{ $t('subscriptions.views.SubscriptionSignaturesConsumption.title') }}</h3>
          <p class="text--x-small text-text-tertiary">{{ $t('subscriptions.views.SubscriptionSignaturesConsumption.subtitle') }}</p>
        </div>

        <template v-if="signaturesConsumptionError">
          <app-alert-in-page
            :dismissible="false"
            no-icon
            variant="error"
          >
            {{ $t('subscriptions.views.SubscriptionSignaturesConsumption.loadError') }}
          </app-alert-in-page>
        </template>

        <template v-else>
          <template v-if="!subscription || signaturesConsumptionPending">
            <AppDataTableSkeletonLoader class="w-100" />
          </template>

          <template v-else>
            <div class="d-flex flex-column align-self-start SubscriptionSignaturesConsumption-dateRange" data-locator="signatures-consumption-date-range">
              <AppDateRangePicker
                :label="$t('subscriptions.views.SubscriptionSignaturesConsumption.inputDateLabel')"
                :model-value="signaturesDateModel"
                @update:model-value="handleDateRangeChange"
              />
            </div>
            <div class="overflow-hidden rounded-lg billing-table-container" data-locator="signatures-consumption-card">
              <div v-if="tableItems.length === 0" class="pa-12 text-center" data-locator="empty-state">
                <div class="d-flex flex-column align-center">
                  <h2 class="text-h6 font-weight-bold mb-3 text-text-primary">
                    {{ $t('subscriptions.views.SubscriptionSignaturesConsumption.emptyState.title') }}
                  </h2>
                  <p class="text-body-2 text-text-secondary ma-0">
                    {{ $t('subscriptions.views.SubscriptionSignaturesConsumption.emptyState.description') }}
                  </p>
                </div>
              </div>

              <template v-else>
                <v-data-table
                  :headers="headers"
                  :items="tableItems"
                  :items-per-page="-1"
                  hide-default-footer
                  class="SubscriptionSignaturesConsumption-table"
                  data-locator="signatures-consumption-table"
                >
                  <template v-if="isBillingByEnvelope" v-slot:[`item.simpleEnvelopes`]="{ item }">
                    <div class="text-left">
                      <div>{{ item.simpleEnvelopes.count }}</div>
                      <div class="text--x-small text-grey-darken-2">{{ item.simpleEnvelopes.documents }} {{ $t('common.document', { count: item.simpleEnvelopes.documents }) }}</div>
                    </div>
                  </template>

                  <template v-if="isBillingByEnvelope" v-slot:[`item.advancedEnvelopes`]="{ item }">
                    <div class="text-left">
                      <div>{{ item.advancedEnvelopes.count }}</div>
                      <div class="text--x-small text-grey-darken-2">{{ item.advancedEnvelopes.documents }} {{ $t('common.document', { count: item.advancedEnvelopes.documents }) }}</div>
                    </div>
                  </template>

                  <template v-slot:[`body.append`]>
                    <tr class="SubscriptionSignaturesConsumption-totalRow" data-locator="total-row">
                      <td class="text-start">
                        <div class="d-flex align-center">
                          <span class="font-weight-bold mr-2">{{ $t('subscriptions.views.SubscriptionSignaturesConsumption.total') }}</span>
                          <AppInfoTooltip>
                            {{ $t('subscriptions.views.SubscriptionSignaturesConsumption.totalTooltip') }}
                          </AppInfoTooltip>
                        </div>
                      </td>
                      <td class="text-left">
                        <template v-if="isBillingByEnvelope">
                          <div>{{ totals.simpleEnvelopes }}</div>
                          <div class="text-caption text-grey-darken-2">{{ totals.simpleDocuments }} {{ $t('common.document', { count: totals.simpleDocuments }) }}</div>
                        </template>
                        <template v-else>{{ totals.simpleSignatures }}</template>
                      </td>
                      <td class="text-left">
                        <template v-if="isBillingByEnvelope">
                          <div>{{ totals.advancedEnvelopes }}</div>
                          <div class="text-caption text-grey-darken-2">{{ totals.advancedDocuments }} {{ $t('common.document', { count: totals.advancedDocuments }) }}</div>
                        </template>
                        <template v-else>{{ totals.advancedSignatures }}</template>
                      </td>
                      <td class="text-left">{{ totals.qualifiedSignatures }}</td>
                      <td class="text-left"><span class="font-weight-bold">{{ totals.totalSignatures }}</span></td>
                      <td class="text-left">{{ formatPrice(totals.totalHT) }}</td>
                      <td class="text-left"><span class="font-weight-bold">{{ formatPrice(totals.totalTTC) }}</span></td>
                    </tr>
                  </template>
                </v-data-table>
              </template>
            </div>
          </template>
        </template>
      </AppCard>
      <!-- envelopes packs -->
      <template v-for="pack in envelopesPacks" :key="pack.id">
        <AppCard
          v-if="envelopesPacks && envelopesPacks.length"
          class="rounded-lg pa-6"
          elevation="0"
        >
          <SubscriptionConsumptionPacksTemplate
            :pack="pack"
            :is-signature-pack="false"
            :data-locator="`envelope-pack-${pack.id}`"
          />
        </AppCard>
      </template>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
import { toast } from '@/common/utils/toast'
import { GET_SIGNATURES_CONSUMPTION } from '@/store/modules/subscription/action_types'
import SubscriptionConsumptionPacksTemplate from '@/subscriptions/views/SubscriptionConsumptionPacksTemplate.vue'

const TVA_RATE = 1.2
const priceFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
})

export default {
  name: 'SubscriptionSignaturesConsumption',
  components: {
    AppCard,
    AppDataTableSkeletonLoader,
    AppDateRangePicker,
    AppInfoTooltip,
    SubscriptionConsumptionPacksTemplate,
  },
  data () {
    const defaultRange = {
      startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    }
    return {
      signaturesDate: { ...defaultRange },
      signaturesDateModel: [defaultRange.startDate, defaultRange.endDate],
    }
  },
  computed: {
    ...mapState('subscription', [
      'subscription',
      'signaturesConsumptionPending',
      'signaturesConsumption',
      'signaturesConsumptionError',
    ]),
    ...mapState('user', ['profile']),
    isBillingByEnvelope () {
      return this.profile?.isBillingByEnvelope || false
    },
    headers () {
      if (this.isBillingByEnvelope) {
        return [
          {
            title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.project'),
            key: 'project',
            sortable: false,
          },
          {
            title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.simpleEnvelopes'),
            key: 'simpleEnvelopes',
            sortable: false,
          },
          {
            title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.advancedEnvelopes'),
            key: 'advancedEnvelopes',
            sortable: false,
          },
          {
            title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.qualifiedSignatures'),
            key: 'qualifiedSignatures',
            sortable: false,
          },
          {
            title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.totalSignatures'),
            key: 'totalSignatures',
            sortable: false,
          },
          {
            title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.totalHT'),
            key: 'totalHT',
            sortable: false,
          },
          {
            title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.totalTTC'),
            key: 'totalTTC',
            sortable: false,
          },
        ]
      }

      return [
        {
          title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.project'),
          key: 'project',
          sortable: false,
        },
        {
          title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.simpleSignatures'),
          key: 'simpleSignatures',
          sortable: false,
        },
        {
          title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.advancedSignatures'),
          key: 'advancedSignatures',
          sortable: false,
        },
        {
          title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.qualifiedSignatures'),
          key: 'qualifiedSignatures',
          sortable: false,
        },
        {
          title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.totalSignatures'),
          key: 'totalSignatures',
          sortable: false,
        },
        {
          title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.totalHT'),
          key: 'totalHT',
          sortable: false,
        },
        {
          title: this.$t('subscriptions.views.SubscriptionSignaturesConsumption.headers.totalTTC'),
          key: 'totalTTC',
          sortable: false,
        },
      ]
    },
    signaturesData () {
      if (!this.signaturesConsumption?.results) {
        return []
      }
      return this.signaturesConsumption.results.map(project => {
        const cost = Number(project.stats?.cost?.cost) || 0

        if (this.isBillingByEnvelope) {
          return {
            project: project.name,
            simpleEnvelopes: {
              count: project.envelopesSimpleCount || 0,
              documents: project.documentsSimpleCount || 0,
            },
            advancedEnvelopes: {
              count: project.envelopesAdvancedCount || 0,
              documents: project.documentsAdvancedCount || 0,
            },
            qualifiedSignatures: project.envelopesQualifiedCount || 0,
            totalSignatures: project.envelopesCount || 0,
            totalHT: cost,
            totalTTC: cost * TVA_RATE,
          }
        }

        return {
          project: project.name,
          simpleSignatures: project.documentsSimpleCount || 0,
          advancedSignatures: project.documentsAdvancedCount || 0,
          qualifiedSignatures: project.documentsQualifiedCount || 0,
          totalSignatures: project.documentsCount || 0,
          totalHT: cost,
          totalTTC: cost * TVA_RATE,
        }
      })
    },
    totals () {
      if (!this.signaturesConsumption) {
        return {
          simpleSignatures: 0,
          advancedSignatures: 0,
          simpleEnvelopes: 0,
          advancedEnvelopes: 0,
          simpleDocuments: 0,
          advancedDocuments: 0,
          qualifiedSignatures: 0,
          totalSignatures: 0,
          totalHT: 0,
          totalTTC: 0,
        }
      }

      const totalCost = Number(this.signaturesConsumption.totalCost) || 0
      return {
        simpleSignatures: this.signaturesConsumption.total?.documentsSimple || 0,
        advancedSignatures: this.signaturesConsumption.total?.documentsAdvanced || 0,
        simpleEnvelopes: this.signaturesConsumption.total?.envelopesSimple || 0,
        advancedEnvelopes: this.signaturesConsumption.total?.envelopesAdvanced || 0,
        simpleDocuments: this.signaturesConsumption.total?.documentsSimple || 0,
        advancedDocuments: this.signaturesConsumption.total?.documentsAdvanced || 0,
        qualifiedSignatures: this.signaturesConsumption.total?.documentsQualified || 0,
        totalSignatures: this.signaturesConsumption.total?.documents || 0,
        totalHT: totalCost,
        totalTTC: totalCost * TVA_RATE,
      }
    },
    tableItems () {
      if (this.isBillingByEnvelope) {
        return this.signaturesData.map(item => ({
          project: item.project,
          simpleEnvelopes: item.simpleEnvelopes,
          advancedEnvelopes: item.advancedEnvelopes,
          qualifiedSignatures: item.qualifiedSignatures,
          totalSignatures: item.totalSignatures,
          totalHT: this.formatPrice(item.totalHT),
          totalTTC: this.formatPrice(item.totalTTC),
        }))
      }

      return this.signaturesData.map(item => ({
        ...item,
        totalHT: this.formatPrice(item.totalHT),
        totalTTC: this.formatPrice(item.totalTTC),
      }))
    },
    envelopesPacks () {
      if (!this.signaturesConsumption?.pack) {
        return []
      }

      // Transformer les données de l'API au format attendu par SubscriptionConsumptionPacksTemplate
      return this.signaturesConsumption.pack.map(pack => ({
        id: pack.id,
        consumed: pack.quantity_start - pack.quantity_left,
        total: pack.quantity_start,
        from: pack.from,
        to: pack.to,
        type: this.getPackType(pack.to),
      }))
    },
  },
  watch: {
    'subscription.id': {
      immediate: true,
      handler (newId) {
        if (newId && !this.signaturesConsumptionPending) {
          this.loadSignaturesConsumption()
        }
      },
    },
  },
  methods: {
    ...mapActions('subscription', [GET_SIGNATURES_CONSUMPTION]),
    async loadSignaturesConsumption () {
      try {
        await this.GET_SIGNATURES_CONSUMPTION(this.signaturesDate)
      } catch (error) {
        console.error('Error loading signatures consumption:', error)
        toast.error(this.$t('subscriptions.views.SubscriptionSignaturesConsumption.loadError'))
      }
    },
handleDateRangeChange (value) {
  const range = Array.isArray(value) ? value : []
  const [startDate, endDate] = range

  this.signaturesDateModel = range
  this.signaturesDate = {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  }

  this.loadSignaturesConsumption()
},
    getPackType (toDate) {
      if (!toDate) return 'mixed'
      const now = dayjs()
      const endDate = dayjs(toDate)
      return endDate.isBefore(now) ? 'expired' : 'mixed'
    },
    formatPrice (value) {
      const numValue = Number(value)
      if (Number.isNaN(numValue)) {
        return '0,00 €'
      }
      return priceFormatter.format(numValue)
    },
  },
}
</script>

<style scoped lang="scss">

.SubscriptionSignaturesConsumption-dateRange {
  width: 260px;
}

.SubscriptionSignaturesConsumption-table {
  :deep(.v-table__wrapper) {
    border: none;
  }

  :deep(table) {
    border-spacing: 0;
    font-size: 14px !important;
  }

  :deep(thead tr th) {
    background-color: rgb(var(--v-theme-surface-secondary));
    border-bottom: 1px solid rgb(var(--v-theme-border-primary));
    font-weight: 500;
    white-space: nowrap;
    padding: 12px !important;
    height: auto !important;
    color: rgb(var(--v-theme-text-primary));
  }

  :deep(tbody tr td) {
    border-bottom: 1px solid rgb(var(--v-theme-border-primary));
    color: rgb(var(--v-theme-text-secondary));
    white-space: nowrap;
    padding: 8px 12px !important;
  }

  :deep(.SubscriptionSignaturesConsumption-totalRow) {
    background-color: rgb(var(--v-theme-surface-secondary));

    td {
      font-weight: 500;
      border-bottom: none;
      color: rgb(var(--v-theme-text-primary));
    }
  }
}

.billing-table-container {
  border: 1px solid rgb(var(--v-theme-border-primary));
}
</style>

